import type { CnmcFilingProfile } from "./cnmcTypes";

type FilingAddress = Pick<
  CnmcFilingProfile,
  | "street"
  | "street_number"
  | "staircase"
  | "postal_code"
  | "municipality"
  | "province"
>;

/** Extracts editable defaults from comma-separated Spanish addresses with a postcode and municipality. */
export function parseCnmcAddress(
  address: string | null | undefined
): Partial<FilingAddress> {
  const parts = (address ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const postalPattern = /^((?:0[1-9]|[1-4]\d|5[0-2])\d{3})\s+(.+)$/;
  const localityIndex = parts.findIndex((part) => postalPattern.test(part));
  if (localityIndex < 0) return {};

  const [, postalCode, municipality] =
    parts[localityIndex].match(postalPattern)!;
  const provinces = parts
    .slice(localityIndex + 1)
    .filter((part) => !/^(?:España|Spain|ES)$/i.test(part));
  const street = parts
    .slice(0, localityIndex)
    .join(", ")
    .match(
      /^(.+?)(?:,\s*|\s+)(?:n[º°.]\s*)?(\d+[a-z]?(?:-\d+[a-z]?)?|s\/n)(?:,\s*(.*))?$/i
    );
  // CNMC's Esc field is a staircase, not a floor or apartment number.
  const staircase = street?.[3]
    ?.split(",")
    .map((part) => part.trim())
    .find((part) => /^esc(?:alera)?[.\s]/i.test(part))
    ?.replace(/^esc(?:alera)?[.\s]+/i, "");

  return {
    postal_code: postalCode,
    municipality,
    ...(provinces.length === 1 ? { province: provinces[0] } : {}),
    ...(street ? { street: street[1], street_number: street[2] } : {}),
    ...(staircase ? { staircase } : {}),
  };
}
